<script setup lang="ts">
import { ref, type Ref, watch } from 'vue'
import { UPDATE_PASSWORD } from '../../graphql/mutations'
import { useMutation } from '@vue/apollo-composable'

// v-model
const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const dialog = ref(props.modelValue)

watch(
  () => props.modelValue,
  (val) => (dialog.value = val)
)

watch(dialog, (val) => emit('update:modelValue', val))

// mutation
const { mutate, loading, onDone, onError } = useMutation(UPDATE_PASSWORD)

// form state
const currPass: Ref<string> = ref('')
const newPass: Ref<string> = ref('')
const confirmPass: Ref<string> = ref('')
const formValid = ref(false)
const showPassword = ref(false)

// ui state
const errorMessage = ref<string | null>(null)
const snackbar = ref(false)

function resetForm() {
  currPass.value = ''
  newPass.value = ''
  confirmPass.value = ''
  showPassword.value = false
  formValid.value = false
  errorMessage.value = null
}

const rules = {
  required: (v: string) => !!v || 'This field is required',
  min: (v: string) => v.length >= 8 || 'Password must be at least 8 characters',
  match: (v: string) => v === newPass.value || 'Passwords do not match',
}

function togglePassword() {
  showPassword.value = !showPassword.value
}

function handlePasswordUpdate() {
  errorMessage.value = null

  mutate({
    input: {
      currentPassword: currPass.value,
      password: newPass.value,
      passwordConfirmation: confirmPass.value,
    },
  })
}

onDone(() => {
  snackbar.value = true
  resetForm()
  dialog.value = false
})

onError((error) => {
  // safe generic message (or parse GraphQL errors if you want)
  errorMessage.value =
    error.graphQLErrors?.[0]?.message ||
    'Unable to update password. Please try again.'
})
</script>

<template>
  <v-dialog
    v-model="dialog"
    attach="body"
    max-width="700"
    persistent
  >
    <v-card class="px-8 pt-6 pb-2 mx-auto" width="700">
      <v-card-title class="text-primary mb-4">
        <v-icon color="primary" class="mr-4">mdi-key</v-icon>
        Change Password
      </v-card-title>


      <v-form v-model="formValid">
        <v-text-field
          v-model="currPass"
          label="Current Password"
          variant="outlined"
          :type="showPassword ? 'text' : 'password'"
          :rules="[rules.required]"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          :disabled="loading"
          @click:append-inner="togglePassword"
        />

        <v-text-field
          v-model="newPass"
          label="New Password"
          variant="outlined"
          :type="showPassword ? 'text' : 'password'"
          :rules="[rules.required, rules.min]"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          :disabled="loading"
          @click:append-inner="togglePassword"
        />

        <v-text-field
          v-model="confirmPass"
          label="Confirm Password"
          variant="outlined"
          :type="showPassword ? 'text' : 'password'"
          :rules="[rules.required, rules.match]"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          :disabled="loading"
          @click:append-inner="togglePassword"
        />
      </v-form>

      <!-- Error -->
      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        class="my-4"
      >
        {{ errorMessage }}
      </v-alert>

      <v-card-actions>
        <v-spacer />

        <v-btn
          variant="text"
          :disabled="loading"
          @click="() => { resetForm(); dialog = false }"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          variant="flat"
          :loading="loading"
          :disabled="!formValid || loading"
          @click="handlePasswordUpdate"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Success snackbar -->
  <v-snackbar
    v-model="snackbar"
    color="success"
    timeout="3000"
  >
    Password updated successfully
  </v-snackbar>
</template>
